import {
    IconCircleDot,
    IconExclamationCircle,
    IconHistory,
    IconPlayerPlay,
    IconPlayerStop,
    IconTrash,
} from '@tabler/icons-react';
import { Endpoints } from '@xver/apis';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import type { LogStreamStatus } from '@xver/hooks/useLogStream';
import useLogStream, { buildKuferContainerLogsUrl } from '@xver/hooks/useLogStream';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LogsTabPanelProps {
    containerId: string;
}

const STATUS_STYLES: Record<LogStreamStatus, { label: string; color: string }> = {
    idle: { label: 'Idle', color: 'var(--color-status-neutral)' },
    connecting: { label: 'Connecting', color: 'var(--color-status-warning)' },
    open: { label: 'Streaming', color: 'var(--color-status-success)' },
    closed: { label: 'Closed', color: 'var(--color-status-neutral)' },
    error: { label: 'Error', color: 'var(--color-status-danger)' },
};

const INITIAL_TAIL_LINES = 120;
const HISTORY_TAIL_LINES = 2000;

export default function LogsTabPanel({ containerId }: LogsTabPanelProps) {
    const { t } = useTranslation('containerEngineInstance');
    const [streaming, setStreaming] = useState(true);
    const preRef = useRef<HTMLPreElement>(null);

    const requestTicket = useCallback(
        () => Endpoints.v1.Kufer.Containers.requestContainerLogTicket(containerId),
        [containerId]
    );

    const buildWsUrl = useCallback(
        (ticket: string) => buildKuferContainerLogsUrl(containerId, ticket),
        [containerId]
    );

    const { logs, status, error, clear } = useLogStream({
        enabled: streaming,
        requestTicket,
        buildWsUrl,
        maxLines: 2000,
    });

    const [historyBlock, setHistoryBlock] = useState<string | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        const el = preRef.current;
        if (!el) return;
        if (el.scrollHeight - el.clientHeight - el.scrollTop < 40) {
            el.scrollTop = el.scrollHeight;
        }
    }, [logs]);

    useEffect(() => {
        if (historyBlock === null) return;
        const el = preRef.current;
        if (el) el.scrollTop = 0;
    }, [historyBlock]);

    const loadEarlier = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const res = await Endpoints.v1.Kufer.Containers.getContainerLogs(containerId, {
                tail_lines: HISTORY_TAIL_LINES,
            });
            setHistoryBlock(res.logs ?? '');
            clear();
        } catch {
        } finally {
            setLoadingHistory(false);
        }
    }, [containerId, clear]);

    const handleClear = useCallback(() => {
        clear();
        setHistoryBlock(null);
    }, [clear]);

    const statusStyle = STATUS_STYLES[status] ?? STATUS_STYLES.idle;

    return (
        <DialogBox
            label={t('logs.title', 'Container Logs')}
            description={t('logs.description', 'Real-time log stream from this container.')}
            rightDescription={
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                        <IconCircleDot size={12} style={{ color: statusStyle.color }} />
                        <span className="text-xs font-medium" style={{ color: statusStyle.color }}>
                            {statusStyle.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {streaming ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconPlayerStop size={14} />}
                                onClick={() => setStreaming(false)}
                            >
                                {t('logs.stop', 'Stop')}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                leftIcon={<IconPlayerPlay size={14} />}
                                onClick={() => setStreaming(true)}
                            >
                                {t('logs.start', 'Start')}
                            </Button>
                        )}
                        {!historyBlock && logs.length >= INITIAL_TAIL_LINES && (
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconHistory size={14} />}
                                onClick={loadEarlier}
                                disabled={loadingHistory}
                            >
                                {loadingHistory
                                    ? t('logs.loadingEarlier', 'Loading earlier logs…')
                                    : t('logs.loadEarlier', 'Load earlier logs')}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconTrash size={14} />}
                            onClick={handleClear}
                        >
                            {t('logs.clear', 'Clear')}
                        </Button>
                    </div>
                </div>
            }
            classNames=""
        >
            <div className="w-full flex flex-col">
                {error && (
                    <div className="w-full px-4 py-2 flex items-center gap-2 bg-red-950/40 light:bg-red-50 border-b-[0.5px] border-line text-red-300 light:text-red-700">
                        <IconExclamationCircle size={16} />
                        <span className="text-xs font-mono">{error}</span>
                    </div>
                )}
                <pre
                    ref={preRef}
                    tabIndex={0}
                    className="w-full h-[60vh] min-h-105 m-0 p-4 bg-black text-white/80 font-mono text-xs leading-relaxed overflow-auto whitespace-pre-wrap wrap-break-word"
                >
                    {logs.length === 0 && !historyBlock
                        ? streaming
                            ? t('logs.emptyStreaming', 'Waiting for log lines…')
                            : t('logs.emptyIdle', 'No log lines. Press Start to begin streaming.')
                        : [historyBlock, logs.join('\n')].filter(Boolean).join('\n')}
                </pre>
                <div className="w-full px-4 py-2 flex justify-between items-center border-t-[0.5px] border-line text-xs text-fg/68">
                    <span>{t('logs.lineCount', '{{count}} lines', { count: logs.length })}</span>
                    <span>
                        {loadingHistory
                            ? t('logs.loadingEarlier', 'Loading earlier logs…')
                            : t('logs.tail', 'Streaming latest logs')}
                    </span>
                </div>
            </div>
        </DialogBox>
    );
}
